import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Coins, Flame, Radio, Lock, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function useIsAnyoneLive() {
  const [live, setLive] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/live");
        if (!cancelled) setLive(Array.isArray(data) ? data.length : 0);
      } catch { /* silent */ }
    };
    check();
    const t = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return live;
}

/**
 * Golden Ticket — one-tap reveal.
 * Renders a gold foil card. User clicks it, we call /scratch/scratch, prize animates in.
 * Odds: 65% 10c / 20% 25c / 10% 50c / 4.5% 200c / 0.5% 5,000c jackpot. Real prizes.
 * 7-day streak guarantees ≥ 200c. Live-bonus checkbox doubles when any stream is live.
 */
export default function GoldenTicketModal({ open, onOpenChange, onClaimed }) {
  const { refresh } = useAuth();
  const [available, setAvailable] = useState(true);
  const [paywall, setPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState("");
  const [sundayOnly, setSundayOnly] = useState(false);
  const [sundayReason, setSundayReason] = useState("");
  const [ticket, setTicket] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [useLiveBonus, setUseLiveBonus] = useState(false);
  const liveCount = useIsAnyoneLive();

  const loadState = useCallback(async () => {
    try {
      const { data } = await api.get("/scratch/today");
      setSundayOnly(!!data.sunday_only);
      setSundayReason(data.sunday_only ? (data.reason || "") : "");
      setPaywall(!!data.paywall);
      setPaywallReason(data.reason || "");
      setAvailable(!!data.available);
      if (data.ticket) {
        setTicket(data.ticket);
        setRevealed(true);
      } else {
        setTicket(null);
        setRevealed(false);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    setUseLiveBonus(false);
    loadState();
  }, [open, loadState]);

  const reveal = async () => {
    if (busy || revealed) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scratch/scratch", { live_bonus: useLiveBonus });
      setTicket(data.ticket);
      setRevealed(true);
      if (refresh) refresh();
      onClaimed && onClaimed(data.ticket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open ticket.");
    } finally {
      setBusy(false);
    }
  };

  const t = ticket;
  const isWin = t && t.coins > 0;
  const isJackpot = t && t.is_jackpot;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass rounded-none max-w-md border-amber-400/40 max-h-[90vh] overflow-y-auto"
        data-testid="golden-ticket-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles size={20} className="text-amber-400" />
            Daily Golden Ticket
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            One free tap per day · Real coins, real odds
          </DialogDescription>
        </DialogHeader>

        {!available && !revealed && !paywall && !sundayOnly && (
          <div className="p-6 text-center">
            <div className="text-6xl mb-3">🎟️</div>
            <div className="font-display text-xl mb-2">You've already opened today's ticket.</div>
            <div className="text-sm text-zinc-500">Come back next Sunday for a fresh one.</div>
          </div>
        )}

        {sundayOnly && (
          <div
            className="p-6 text-center border border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-black to-black"
            data-testid="ticket-sunday-only"
          >
            <div className="text-6xl mb-3">📅</div>
            <div className="font-display text-2xl mb-2">Sundays only</div>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-sm mx-auto">
              {sundayReason || "The Golden Ticket only appears on Sundays. Come back this Sunday for your free spin."}
            </p>
          </div>
        )}

        {paywall && (
          <div
            className="p-6 text-center relative overflow-hidden border border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-black to-black"
            data-testid="ticket-paywall"
          >
            <div className="absolute -top-8 -right-8 text-amber-400/10 text-[160px] leading-none pointer-events-none">
              🎟️
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400 border border-amber-400/40 px-3 py-1 mb-4">
                <Lock size={11} /> Members only
              </div>
              <h3 className="font-display text-2xl mb-3">Unlock the daily Golden Ticket.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-sm mx-auto mb-6">
                {paywallReason || "Daily tickets are for paying members. Grab any coin pack to unlock — the tickets pay you back many times over."}
              </p>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-4">
                🌱 10c · 🔥 25c · 💎 50c · 🍾 200c · 🏆 5,000c jackpot
              </div>
              <Button
                asChild
                className="btn-crimson rounded-none px-6 py-6"
                data-testid="ticket-paywall-cta"
              >
                <Link to="/coins" onClick={() => onOpenChange(false)}>
                  <Coins size={14} className="mr-2" /> Buy a coin pack to unlock
                </Link>
              </Button>
            </div>
          </div>
        )}

        {!paywall && !sundayOnly && (available || revealed) && (
          <>
            {liveCount > 0 && !revealed && (
              <label
                className="flex items-center justify-between gap-3 border border-rose-600/40 bg-rose-950/20 px-3 py-2.5 cursor-pointer"
                data-testid="ticket-live-bonus"
              >
                <div className="flex items-center gap-2">
                  <Radio size={16} className="text-rose-500" />
                  <div>
                    <div className="text-sm font-medium">Live bonus — double your prize</div>
                    <div className="text-[11px] text-zinc-500">
                      {liveCount} stream{liveCount === 1 ? "" : "s"} live now
                    </div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={useLiveBonus}
                  onChange={(e) => setUseLiveBonus(e.target.checked)}
                  className="accent-rose-500 w-5 h-5"
                />
              </label>
            )}

            <style>{`
              @keyframes ticket-shimmer {
                0%   { background-position: 0% 50%; }
                50%  { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
              @keyframes ticket-reveal-in {
                0%   { transform: scale(0.6) rotateY(90deg); opacity: 0; }
                60%  { transform: scale(1.08) rotateY(0deg); opacity: 1; }
                100% { transform: scale(1) rotateY(0deg); opacity: 1; }
              }
              .ticket-gold {
                background: linear-gradient(120deg,
                  #b57e10 0%, #f9d423 25%, #ffffff 50%, #f9d423 75%, #b57e10 100%);
                background-size: 200% 200%;
                animation: ticket-shimmer 3s ease-in-out infinite;
              }
              .ticket-prize-in {
                animation: ticket-reveal-in 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
              }
            `}</style>

            {!revealed && available && (
              <button
                type="button"
                onClick={reveal}
                disabled={busy}
                data-testid="ticket-tap-to-reveal"
                className="w-full aspect-[16/9] ticket-gold border-2 border-amber-500 flex flex-col items-center justify-center text-center px-6 hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:cursor-wait relative overflow-hidden"
              >
                <div className="absolute inset-0 pointer-events-none opacity-25"
                     style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.6) 0px, transparent 3px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.5) 0px, transparent 2px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.4) 0px, transparent 2px)" }}
                />
                <div className="text-5xl mb-2 drop-shadow-lg">{busy ? "" : "🎟️"}</div>
                <div className="font-display text-3xl text-black/85 drop-shadow-sm">
                  {busy ? (
                    <span className="flex items-center gap-2 text-black/70">
                      <Loader2 size={22} className="animate-spin" /> Opening…
                    </span>
                  ) : "TAP TO REVEAL"}
                </div>
                {!busy && (
                  <div className="text-[11px] uppercase tracking-widest text-black/60 mt-1">
                    Your daily Golden Ticket
                  </div>
                )}
              </button>
            )}

            {revealed && t && (
              <div
                key={t.id}
                className={`ticket-prize-in w-full aspect-[16/9] border-2 flex flex-col items-center justify-center px-6 text-center ${
                  isJackpot
                    ? "border-amber-400 bg-gradient-to-br from-amber-900/60 via-black to-amber-950 shadow-[0_0_80px_rgba(251,191,36,0.4)_inset]"
                    : isWin
                      ? "border-emerald-500/50 bg-gradient-to-br from-emerald-950 via-black to-emerald-950/60"
                      : "border-white/10 bg-black"
                }`}
                data-testid="ticket-prize"
              >
                <div className={`text-6xl mb-2 ${isJackpot ? "animate-bounce" : ""}`}>
                  {isJackpot ? "🏆" : isWin ? "🎉" : "🎟️"}
                </div>
                <div
                  className={`font-display text-4xl mb-1 ${isJackpot ? "text-amber-400" : "text-white"}`}
                  data-testid="ticket-prize-label"
                >
                  +{t.coins.toLocaleString()} coins
                </div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-400">
                  {isJackpot ? "JACKPOT" : t.prize_label}
                </div>
                {t.is_live_bonus && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-rose-400 flex items-center gap-1">
                    <Radio size={10} /> Live bonus x2 applied
                  </div>
                )}
                {t.is_streak_reward && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1">
                    <Flame size={10} /> 7-day streak bonus
                  </div>
                )}
              </div>
            )}

            <div className="text-[10px] uppercase tracking-widest text-zinc-500 text-center pt-2">
              Odds: 🌱 10c · 🔥 25c · 💎 50c · 🍾 200c · 🏆 5,000c jackpot
            </div>

            {revealed && (
              <div className="flex justify-center pt-2">
                <Button
                  onClick={() => onOpenChange(false)}
                  className="btn-crimson rounded-none px-6 py-5"
                  data-testid="ticket-close-button"
                >
                  <Coins size={14} className="mr-2" /> Sweet — keep going
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
